import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { api } from '../services/api';

type Props = {
  token: string;
  onAccept: () => void;
};

export default function TermsScreen({ token, onAccept }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [age, setAge] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const ready = age && accepted;

  /**
   * D-421 — kayıt başarısızsa AKIŞ İLERLEMEZ.
   *
   * Önceki hâl `catch` içinde de `onAccept()` çağırıyordu; yorumu "sonraki
   * adımda tekrar denenebilir" diyordu ama kaynakta **hiçbir yerde tekrar
   * yoktur**: `acceptTerms` yalnız burada çağrılır. Dahası giriş kapısı
   * (`onboarded`) yalnız `users.name` alanına bakar; kullanıcı adını
   * girdiği anda bu ekran bir daha hiç açılmaz. Sonuç, `terms_accepted_at`
   * ve `age_confirmed` kalıcı olarak boş kalan, buna rağmen platformda tam
   * etkin bir hesaptı.
   *
   * Yaş onayı ve koşul kabulü hukuki kayıttır (`LEGAL.md`, UK Ltd). Kaydı
   * alınmamış bir onayı alınmış saymak, tıkanmayı önlemekten daha pahalıdır:
   * kullanıcı ilerlemek yerine yeniden dener.
   */
  async function handleContinue() {
    if (!ready) return;
    setLoading(true);
    try {
      await api.acceptTerms(token);
      onAccept();
    } catch {
      Alert.alert(t('common.error'), t('terms.saveFailed'));
    } finally {
      setLoading(false);
    }
  }

  function showDoc() {
    Alert.alert('', t('terms.docPlaceholder'));
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top + spacing.xl * 2 }]}>
      <Text style={styles.title}>{t('terms.title')}</Text>

      <TouchableOpacity
        style={styles.row}
        onPress={() => setAge((v) => !v)}
        activeOpacity={0.7}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: age }}>
        <View style={[styles.box, age && styles.boxChecked]}>
          {age && <Text style={styles.check}>✓</Text>}
        </View>
        <Text style={styles.rowText}>{t('terms.age')}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.row}
        onPress={() => setAccepted((v) => !v)}
        activeOpacity={0.7}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: accepted }}>
        <View style={[styles.box, accepted && styles.boxChecked]}>
          {accepted && <Text style={styles.check}>✓</Text>}
        </View>
        <Text style={styles.rowText}>
          <Text style={styles.link} onPress={showDoc}>{t('terms.termsLink')}</Text>
          {' & '}
          <Text style={styles.link} onPress={showDoc}>{t('terms.privacyLink')}</Text>
          {' — '}
          {t('terms.accept')}
        </Text>
      </TouchableOpacity>

      <View style={styles.spacer} />

      <PrimaryActionButton
        testID="onboarding-terms-continue"
        label={t('terms.continue')}
        style={{ marginBottom: insets.bottom + spacing.lg }}
        disabled={!ready || loading}
        loading={loading}
        onPress={handleContinue}
      />
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    paddingHorizontal: spacing.lg,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.xl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: spacing.md,
  },
  box: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.gray400,
    marginRight: spacing.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 1,
  },
  boxChecked: {
    backgroundColor: colors.black,
    borderColor: colors.black,
  },
  check: { color: colors.white, fontSize: 15, fontWeight: '800' },
  rowText: { flex: 1, fontSize: fontSize.md, color: colors.black, lineHeight: 22 },
  link: { textDecorationLine: 'underline', fontWeight: '600' },
  spacer: { flex: 1 },
});
